// Public read-only listing of open buy orders for the Browse page.
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const ListInput = z
  .object({ established_buyers_only: z.boolean().optional() })
  .optional();

export const listOpenBuyOrders = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => ListInput.parse(i) ?? {})
  .handler(async ({ data }) => {
    const supa = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: rawRows, error } = await supa
      .from("buy_orders")
      .select("id, title, card_name, tcg, sport, condition_min, card_language, max_price_cents, currency, quantity, created_at, reference_image_path, buyer_id")
      .eq("status", "open")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) {
      console.error("[buy-orders] list failed", error);
      return { orders: [] as any[], error: error.message };
    }
    let rows = rawRows ?? [];

    if (data?.established_buyers_only && rows.length > 0) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const buyerIds = Array.from(new Set(rows.map((r) => r.buyer_id).filter(Boolean) as string[]));
        // Count completed (released) purchases per buyer
        const { data: txns } = await supabaseAdmin
          .from("transactions")
          .select("buyer_id")
          .in("buyer_id", buyerIds)
          .eq("status", "released");
        const completedByBuyer = new Map<string, number>();
        for (const t of txns ?? []) {
          completedByBuyer.set(t.buyer_id, (completedByBuyer.get(t.buyer_id) ?? 0) + 1);
        }
        // Buyer rating average from profiles (90% of 5 = 4.5)
        const { data: profs } = await supabaseAdmin
          .from("profiles")
          .select("id, buyer_rating_avg, buyer_rating_count")
          .in("id", buyerIds);
        const ratingByBuyer = new Map<string, { avg: number; count: number }>();
        for (const p of profs ?? []) {
          ratingByBuyer.set(p.id, { avg: Number(p.buyer_rating_avg ?? 0), count: p.buyer_rating_count ?? 0 });
        }
        const established = new Set(
          buyerIds.filter((id) => {
            const completed = completedByBuyer.get(id) ?? 0;
            const r = ratingByBuyer.get(id);
            if (completed < 3) return false;
            if (!r || r.count === 0) return false;
            return r.avg >= 4.5;
          }),
        );
        rows = rows.filter((r) => r.buyer_id && established.has(r.buyer_id));
      } catch (e) {
        console.error("[buy-orders] established buyers filter failed", e);
      }
    }

    const paths = rows.map((r) => r.reference_image_path).filter((p): p is string => !!p);
    const urlByPath = new Map<string, string>();
    if (paths.length > 0) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: signed } = await supabaseAdmin.storage
          .from("buy-order-images")
          .createSignedUrls(paths, 60 * 60);
        for (const s of signed ?? []) {
          if (s.path && s.signedUrl) urlByPath.set(s.path, s.signedUrl);
        }
      } catch (e) {
        console.error("[buy-orders] batch sign failed", e);
      }
    }
    const orders = rows.map((r) => ({
      ...r,
      reference_image_url: r.reference_image_path ? urlByPath.get(r.reference_image_path) ?? null : null,
    }));
    return { orders, error: null as string | null };
  });

const RecentInput = z.object({
  ids: z.array(z.string().uuid()).max(50),
});

export const getRecentlyViewedOrders = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => RecentInput.parse(i))
  .handler(async ({ data, context }) => {
    if (data.ids.length === 0) return { orders: [] };
    const { data: rows, error } = await context.supabase
      .from("buy_orders")
      .select("id, title, card_name, tcg, status, max_price_cents, currency, created_at")
      .in("id", data.ids);
    if (error) {
      console.error("[buy-orders] recent fetch failed", error);
      return { orders: [] };
    }
    const byId = new Map((rows ?? []).map((r) => [r.id, r]));
    const ordered = data.ids
      .map((id) => byId.get(id))
      .filter((r): r is NonNullable<typeof r> => !!r);
    return { orders: ordered };
  });
